import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import ImageLightbox from '../components/ImageLightbox';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProjectDetail = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [allProjects, setAllProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get(`${API}/projects`);
        setAllProjects(response.data);
        const foundProject = response.data.find(p => p.id === projectId);
        setProject(foundProject);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };
    fetchProjects();
  }, [projectId]);

  // Use images from database
  const images = project?.images?.length > 0 
    ? project.images 
    : (project?.image ? [project.image] : []);

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-56">
        <div className="container mx-auto px-4 text-center">
          <p className="text-xl text-gray-600">Loading project...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-white pt-56">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Project Not Found</h1>
          <Link to="/projects">
            <Button className="bg-orange-600 hover:bg-orange-700">Back to Projects</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <section className="pt-56 pb-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <Link to="/projects" className="inline-flex items-center text-orange-600 hover:text-orange-700 mb-6">
            <LucideIcons.ArrowLeft className="w-5 h-5 mr-2" />
            Back to Projects
          </Link>
          <div className="max-w-4xl">
            <Badge className="bg-orange-600 text-white mb-4">{project.category}</Badge>
            <h1 className="text-5xl font-bold text-gray-900 mb-4">{project.title}</h1>
            <div className="flex items-center space-x-2 text-gray-600 mb-4">
              <LucideIcons.MapPin className="w-5 h-5" />
              <span className="text-xl">{project.location}</span>
            </div>
            <p className="text-xl text-gray-600">{project.description}</p>
          </div>
        </div>
      </section>

      {/* Project Images Gallery */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Project Gallery</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {images.map((img, idx) => (
              <div 
                key={img} 
                className="group relative overflow-hidden rounded-xl shadow-lg cursor-pointer"
                onClick={() => {
                  setLightboxIndex(idx);
                  setLightboxOpen(true);
                }}
              >
                <img 
                  src={img}
                  alt={`${project.title} - Image ${idx + 1}`}
                  className="w-full h-80 object-cover group-hover:scale-110 transition-transform duration-500"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&h=600&fit=crop';
                  }}
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="bg-white/90 rounded-full p-3">
                      <LucideIcons.ZoomIn className="w-6 h-6 text-gray-900" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Image Lightbox */}
      {lightboxOpen && (
        <ImageLightbox
          images={images}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}

      {/* Project Details */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Project Highlights</h2>
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <LucideIcons.CheckCircle2 className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Premium Quality Materials</h3>
                    <p className="text-gray-600">Using only the finest materials for long-lasting results</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <LucideIcons.CheckCircle2 className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Expert Craftsmanship</h3>
                    <p className="text-gray-600">Delivered by our experienced team of professionals</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <LucideIcons.CheckCircle2 className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">On-Time Completion</h3>
                    <p className="text-gray-600">Completed within agreed timeframe and budget</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <LucideIcons.CheckCircle2 className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Client Satisfaction</h3>
                    <p className="text-gray-600">Another happy customer with exceptional results</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other Projects */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">More Projects</h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {allProjects.filter(p => p.id !== projectId).slice(0, 3).map((p) => (
              <Link key={p.id} to={`/projects/${p.id}`}>
                <div className="group cursor-pointer">
                  <div className="relative h-64 overflow-hidden rounded-xl shadow-lg mb-4">
                    <img 
                      src={p.image}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <h3 className="font-semibold text-gray-900 group-hover:text-orange-600 transition-colors">{p.title}</h3>
                  <p className="text-sm text-gray-600">{p.location}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-orange-600 to-orange-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Want Results Like This?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Let us transform your property with the same quality and attention to detail
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-orange-600 hover:bg-gray-100 px-8">
              Start Your Project
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ProjectDetail;